import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';

import {
    commonBinaryStoreWriteSettingControls,
    commonFileTypeStoreDeleteActivityReadSettings,
    commonFileTypeStoreGetMetadataActivityReadSettings,
    commonFileTypeStoreWriteSettingControls,
    getCommonBinaryStoreReadSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    cdcDestination
} from '../consts/metadata-consts';
import { binaryMetadata } from '../dataset/binary-metadata';
import { format } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind, CredentialTypeOptions, ModelConstants, sharePointOnlineAuthTypes } from 'shell-models-lib';
import { dynamicCommonControls } from './sharepoint-online-list-metadata';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';

const servicePrincipalCredentialKeyControl: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'parameterizable',
        attribute: 'servicePrincipalKey',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            isLabelVertical: true,
            description: stringResources.SharePointOnlineList_ServicePrincipalKey_Description,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        }
    },
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.SharePointOnlineList_DisplayText),
        stringResources.ModelName_ServicePrincipalKey)
};
const dynamicServicePrincipalControl: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'servicePrincipalId',
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            label: stringResources.ModelName_ServicePrincipalID,
            description: stringResources.SharePointOnlineList_ServicePrincipalId_Description,
            isLabelVertical: true
        }
    },
    servicePrincipalCredentialKeyControl
];

const authenticationTypeControl: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: [
        {
            label: stringResources.AuthenticationType_ServicePrincipal,
            value: sharePointOnlineAuthTypes.ServicePrincipal
        },
        {
            label: stringResources.ModelName_UserAuthentication,
            value: sharePointOnlineAuthTypes.UserAuth
        }
    ],
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true,
    defaultValue: sharePointOnlineAuthTypes.ServicePrincipal,
    cases: [
        {
            caseValue: sharePointOnlineAuthTypes.ServicePrincipal,
            children: [...dynamicServicePrincipalControl]
        },
        {
            caseValue: sharePointOnlineAuthTypes.UserAuth,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAuth,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                }
            ]
        }
    ]
};

const dynamicSharePointControls: ControlMetadata[] = GlobalVariables.enableConnectorUserAuthCred
    ? dynamicCommonControls.concat([authenticationTypeControl])
    : dynamicCommonControls.concat(dynamicServicePrincipalControl);

export const sharePointOnlineFileMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.sharePointOnlineFile.linkedServiceType,
    displayName: format(stringResources.ModelName_DisplayTextPreviewTemplate, stringResources.SharePointOnlineFile_DisplayText),
    linkedServiceKind: 'file',
    supportedInTrident: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.BuiltIn,
        id: BuiltInDataSourceOptionId.SharepointFolder
    },
    supportedAuthKindsInTrident: [
        ConnectionAuthKind.OAuth2,
        ConnectionAuthKind.WorkspaceIdentity
    ],
    icon: {
        className: 'svg-i-100x100-sharepoint'
    },
    category: ['ServicesApps'],
    isFirstClass: true,
    supportIncrementalBinaryCopy: true,
    supportCopyJobSource: true,
    supportCopyJobSink: true,
    ...(GlobalVariables.copyJobGenericCdcPhase2 ? {
        cdc: {
            supportCDC: true,
            supportedCDCDestinations: cdcDestination,
            supportGenericCdc: true
        }
    } : {}),
    copySourceTypes: [adfModelTypeMap.sharePointOnlineFile.copySourceType],
    copySinkTypes: __IS_FABRIC_DPE__ ? [adfModelTypeMap.sharePointOnlineFile.copySinkType] : [],
    supportIRVersion: '-1', // to do
    supportRootFolder: true,
    supportDatasetV2: true,
    notSupportCopySink: !__IS_FABRIC_DPE__,
    locationPattern: {
        folderPath: '([^]*)/[^/]*',
        fileName: '(?:[^]*/)?([^/]*)'
    },
    mergeReadSettingsInTrident: true,
    locationPropertiesRequireParameters: ['folderPath', 'fileName'],
    supportGetMetadataActivity: true,
    getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
    notSupportDeleteActivity: !__IS_FABRIC_DPE__,
    deleteActivityReadProperties: __IS_FABRIC_DPE__ ? commonFileTypeStoreDeleteActivityReadSettings : undefined,
    showApiNameInLocationPath: true,
    storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(),
    datasetTypeToStoreReadSettingControls: {
        [binaryMetadata.type]: getCommonBinaryStoreReadSettingControls(false, false, false, false, false)
    },
    datasetTypeToStoreWriteSettingControls: __IS_FABRIC_DPE__ ? {
        [ModelConstants.binaryDataset]: commonBinaryStoreWriteSettingControls
    } : undefined,
    storeWriteSettingControls: __IS_FABRIC_DPE__ ? commonFileTypeStoreWriteSettingControls : undefined,
    supportFirstClassParameterExperience: true,
    controls: dynamicSharePointControls
};
